import { useCallback, useEffect, useEffectEvent, useState } from "react";

import { useControls, useTransformEffect, useTransformInit, type ReactZoomPanPinchContext } from "react-zoom-pan-pinch";

import { type FullAirHandlerUnit } from "@eisox/api/core-api";
import { cn } from "@eisox/design-system";
import { ChevronDownIcon, ChevronUpIcon } from "@eisox/icons";

import { m } from "~/paraglide/messages";

import { getNavigationTargets, getViewportRect, type Rect } from "../helpers/group-navigation.helper";

const NAME = "GroupNavigation";

const AIR_HANDLER_UNIT_ID_ATTRIBUTE = "data-air-handler-unit-id";

interface Layout {
  viewport: Rect;
  items: (Pick<Rect, "y" | "height"> & { id: string })[];
}

const measureLayout = (instance: ReactZoomPanPinchContext): Layout | undefined => {
  const { wrapperComponent, contentComponent, transformState } = instance;
  if (!wrapperComponent || !contentComponent) return undefined;
  const { scale } = transformState;
  const contentRect = contentComponent.getBoundingClientRect();
  const items = Array.from(contentComponent.querySelectorAll<HTMLElement>(`[${AIR_HANDLER_UNIT_ID_ATTRIBUTE}]`)).map(
    element => {
      const rect = element.getBoundingClientRect();
      return {
        id: element.getAttribute(AIR_HANDLER_UNIT_ID_ATTRIBUTE) ?? "",
        y: (rect.top - contentRect.top) / scale,
        height: rect.height / scale,
      };
    },
  );
  return {
    viewport: getViewportRect(transformState, {
      width: wrapperComponent.offsetWidth,
      height: wrapperComponent.offsetHeight,
    }),
    items,
  };
};

interface FocusRequest {
  airHandlerUnitId: string;
}

interface GroupNavigationProps {
  airHandlerUnit: FullAirHandlerUnit[];
  focusRequest?: FocusRequest;
}

const GroupNavigation: React.FC<GroupNavigationProps> = ({ airHandlerUnit, focusRequest }) => {
  const { instance, zoomToElement } = useControls();
  const [layout, setLayout] = useState<Layout>();

  const focus = (airHandlerUnitId: string, animationTime?: number) => {
    const element = instance.contentComponent?.querySelector<HTMLElement>(
      `[${AIR_HANDLER_UNIT_ID_ATTRIBUTE}="${CSS.escape(airHandlerUnitId)}"]`,
    );
    if (!element) return;
    // react-zoom-pan-pinch 3.7 cannot cancel a running animation: its frame loop keeps running and,
    // when it ends, aborts the next one. Clearing it and waiting a frame lets that loop stop first.
    instance.animation = null;
    requestAnimationFrame(() => zoomToElement(element, undefined, animationTime));
  };

  const onFocusRequest = useEffectEvent((request: FocusRequest) => focus(request.airHandlerUnitId));

  useTransformInit(({ instance }) => {
    const firstAirHandlerUnit = airHandlerUnit[0];
    if (firstAirHandlerUnit) requestAnimationFrame(() => focus(firstAirHandlerUnit.id, 0));
    setLayout(measureLayout(instance));
  });

  useTransformEffect(
    useCallback(({ instance }) => {
      setLayout(measureLayout(instance));
    }, []),
  );

  useEffect(() => {
    if (focusRequest) onFocusRequest(focusRequest);
  }, [focusRequest]);

  if (!layout) return null;

  const targets = getNavigationTargets(layout.items, layout.viewport);
  const previous = targets.previous === undefined ? undefined : layout.items[targets.previous];
  const next = targets.next === undefined ? undefined : layout.items[targets.next];

  const arrowClassName =
    "absolute left-1/2 flex size-10 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full bg-primary-dark-blue text-primary-white";

  return (
    <>
      {previous && (
        <button
          type="button"
          aria-label={m.previous()}
          className={cn(arrowClassName, "top-2.5")}
          onClick={() => focus(previous.id)}
        >
          <ChevronUpIcon />
        </button>
      )}
      {next && (
        <button
          type="button"
          aria-label={m.next()}
          className={cn(arrowClassName, "bottom-2.5")}
          onClick={() => focus(next.id)}
        >
          <ChevronDownIcon />
        </button>
      )}
    </>
  );
};

GroupNavigation.displayName = NAME;

export { GroupNavigation };
export type { FocusRequest, GroupNavigationProps };
